import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/features/auth/useSession';
import type { SubscriptionStatus } from '@mudong/shared';

export interface ShamanContext {
  shaman: {
    id: string;
    slug: string;
    display_name_ko: string;
    bio: string | null;
    is_active: boolean;
  };
  subscription: {
    status: SubscriptionStatus;
    current_period_start: string | null;
    current_period_end: string | null;
    next_charge_at: string | null;
    canceled_at: string | null;
  } | null;
}

// 한번이라도 결제 주기가 시작된 적이 있는지. 없으면 past_due 는 "실패"가 아니라 "미등록".
export function hasEverPaid(sub: ShamanContext['subscription']): boolean {
  return !!sub?.current_period_start;
}

export function useShamanContext() {
  const { session } = useSession();
  return useQuery({
    queryKey: ['shaman-context', session?.user.id],
    enabled: !!session?.user.id,
    queryFn: async (): Promise<ShamanContext | null> => {
      const { data: shaman, error } = await supabase
        .from('shamans')
        .select('id, slug, display_name_ko, bio, is_active')
        .eq('profile_id', session!.user.id)
        .maybeSingle();
      if (error) throw error;
      if (!shaman) return null;
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('status, current_period_start, current_period_end, next_charge_at, canceled_at')
        .eq('shaman_id', shaman.id)
        .maybeSingle();
      return { shaman, subscription: subscription ?? null };
    },
  });
}

export function isSubscriptionActive(status: SubscriptionStatus | null | undefined): boolean {
  return status === 'active';
}
